import {
  Client,
  BufferTrackerReact as BufferTracker,
  Product,
} from '@buffer-mono/tracking-plan'
import { useCommonTrackingProperties } from '~publish/hooks/useCommonTrackingProperties'

import type {
  CommentData,
  SavedReply,
  GetChannelInfoForCommentsQuery,
} from '~publish/gql/graphql'

type TrackSuggestionUsed = {
  comment: CommentData
  suggestionText: string
}

type TrackSavedReplyUsed = {
  comment: CommentData
  savedReply: SavedReply
}

type ChannelForTracking = Pick<
  NonNullable<GetChannelInfoForCommentsQuery['channel']>,
  'id' | 'service'
>

type TrackCommentsFiltered = {
  channel: ChannelForTracking
  filterType: string
  filterValue: string
}

type TrackCommentsSorted = {
  channel: ChannelForTracking
  sortType: string
  sortValue: string
}

type UseCommentsTracking = {
  trackSuggestionUsed: (payload: TrackSuggestionUsed) => void
  trackSavedReplyUsed: (payload: TrackSavedReplyUsed) => void
  trackCommentsFiltered: (payload: TrackCommentsFiltered) => void
  trackCommentsSorted: (payload: TrackCommentsSorted) => void
}

export const useCommentsTracking = (): UseCommentsTracking => {
  const commonTrackingProperties = useCommonTrackingProperties()

  const trackSuggestionUsed = ({
    comment,
    suggestionText,
  }: TrackSuggestionUsed): void => {
    BufferTracker.suggestionUsed({
      clientName: Client.PublishWeb,
      product: Product.Publish,
      channelId: comment.channelId,
      serviceType: comment.serviceType,
      postId: comment.post.id,
      commentId: comment.id,
      commentText: comment.text,
      suggestionContent: suggestionText,
      ...commonTrackingProperties,
    })
  }

  const trackSavedReplyUsed = ({
    comment,
    savedReply,
  }: TrackSavedReplyUsed): void => {
    BufferTracker.savedReplyUsed({
      clientName: Client.PublishWeb,
      product: Product.Publish,
      channelId: comment.channelId,
      serviceType: comment.serviceType,
      postId: comment.post.id,
      commentId: comment.id,
      savedReplyId: savedReply.id,
      savedReplyName: savedReply.title,
      savedReplyContent: savedReply.content.text,
      savedReplyHotkeys: savedReply.hotkeys?.join(',') ?? '',
      ...commonTrackingProperties,
    })
  }

  const trackCommentsFiltered = ({
    channel,
    filterType,
    filterValue,
  }: TrackCommentsFiltered): void => {
    BufferTracker.commentsFiltered({
      clientName: Client.PublishWeb,
      product: Product.Publish,
      channelId: channel.id,
      serviceType: channel.service,
      filterType,
      filterValue,
      ...commonTrackingProperties,
    })
  }

  const trackCommentsSorted = ({
    channel,
    sortType,
    sortValue,
  }: TrackCommentsSorted): void => {
    BufferTracker.commentsSorted({
      clientName: Client.PublishWeb,
      product: Product.Publish,
      channelId: channel.id,
      serviceType: channel.service,
      sortType,
      sortValue,
      ...commonTrackingProperties,
    })
  }

  return {
    trackSuggestionUsed,
    trackSavedReplyUsed,
    trackCommentsFiltered,
    trackCommentsSorted,
  }
}
